import { randUuid } from '@ngneat/falso'

import type { Creator } from '@/modules/creators/types.ts'
import { sendCustomAppAction } from '@/modules/desktop/DesktopAppProvider.tsx'
import { TrackedFeatures, trackFeatureUsage } from '@/utils/feature-engagement'

export interface ProxyCheckRequest {
  host: string
  username: string
  password: string
  responseChannel: string
}

export interface ProxyCheckResponse {
  success: boolean
  error?: string
}

export const checkProxy = async (
  host: string,
  username: string,
  password: string,
) => {
  return sendProxyRequest({
    host: host,
    username: username,
    password: password,
    responseChannel: randUuid(),
  })
}

const sendProxyRequest = async (
  request: ProxyCheckRequest,
): Promise<ProxyCheckResponse> => {
  if (!window.electronAPI) {
    return Promise.resolve({
      success: false,
      error: 'No electron API found. Please restart the app.',
    })
  }

  return new Promise((resolve, reject) => {
    let resolved = false

    const timeoutId = setTimeout(() => {
      if (!resolved) {
        resolve({
          success: false,
          error:
            'Timeout while communicating to desktop app. Please restart the app.',
        })
      }
    }, 20000)

    window.electronAPI.onMessageFromMainProcessOnce(
      request.responseChannel,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ([response]: any[]) => {
        resolved = true
        clearTimeout(timeoutId)
        resolve(response)
      },
    )

    sendCustomAppAction('check-proxy', request)
  })
}

export const startOnlyFansProfile = (
  creator: Creator,
  openProxyConfigurationDialog: (firstLaunch: boolean) => void,
  setCheckingProxy: (value: boolean) => void,
  setCheckProxyError: (error: string | undefined) => void,
  enableButtonCooldown: () => void,
) => {
  if (creator.proxySettingsOnLaunch || !creator.browserProfile) {
    openProxyConfigurationDialog(true)

    return
  }

  const proxySettings = creator.browserProfile.proxySettings

  if (!proxySettings.proxyType || proxySettings.proxyType === 'NONE') {
    sendStartSession(creator)
    enableButtonCooldown()

    return
  }

  setCheckingProxy(true)
  checkProxy(
    proxySettings.host,
    proxySettings.username,
    proxySettings.password,
  ).then((response) => {
    setCheckingProxy(false)

    if (response.success) {
      sendStartSession(creator)
      enableButtonCooldown()

      return
    }

    setCheckProxyError(response.error)
  })
}

export const sendStartSession = (creator: Creator, url?: string) => {
  trackFeatureUsage(TrackedFeatures.OldOFLauncher, creator.id)
  sendCustomAppAction('start-of-session', { 'creator-id': creator.id, url })
}

export function cloneEvent(event: Record<string, any>) {
  if (event === undefined || event === null) return {}

  const clone: Record<string, any> = {}

  for (const key in event) {
    const d = Object.getOwnPropertyDescriptor(event, key)

    if (d && (d.get || d.set)) Object.defineProperty(clone, key, d)
    else clone[key] = event[key]
  }
  Object.setPrototypeOf(clone, event)

  return clone
}
